import * as R from 'ramda'
import type {
  ManifestDetail,
  PartyType,
  Cargo,
  Container,
  HouseBillOperation,
  BillStatus,
} from '@/typings/detail'
import { PARTY_TYPES } from '@/const/party-types'
import { v4 as uuidv4 } from 'uuid'
import { ContainerIndicator } from '@/const'

/** 固定的parties顺序 - 发货人、收货人、通知人 */
const DEFAULT_PARTIES_ORDER: PartyType[] = [
  PARTY_TYPES.SN,
  PARTY_TYPES.CO,
  PARTY_TYPES.NO,
]

/** 创建舱单风险状态 */
export const createHouseBillRiskStatus = () => {
  return {
    id: '',
    houseBillId: '',
    riskStatus: '',
    riskStatusAt: '',
    productCode: '',
    createTime: '',
  } as BillStatus
}
/** 舱单最新状态 */
export const createHouseBillOperation = () => {
  return {
    id: '',
    houseBillId: '',
    operatorId: '',
    operatorAccount: '',
    operatorNickname: '',
    operationAt: '',
    ackStatusAt: '',
    ackStatus: '',
    remark: '',
    cyberReplyContentId: '',
    cyberSendContentId: '',
    messageReferenceNo: '',
    ackError: '',
    ackErrorEn: '',
    enterpriseId: '',
    enterpriseName: '',
    type: '',
    typeName: '',
    ackName: '',
    ackAt: '',
    ackErrorCode: '',
    ackBadData: '',
    source: '',
    productCode: '',
    createTime: '',
  } as HouseBillOperation
}

/** 创建货物 */
export const createDefaultCargo = (groupId: string): Cargo => {
  return {
    groupId,
    id: '',
    containerNo: '',
    packages: '',
    grossWeight: '',
    undgNumber: '',
    cargoDescription: '',
    markNumbers: '',
    partyEmergencyContact: '',
    partyEmergencyContactPhone: '',
    productCode: '',
    createTime: 0,
  }
}

/** 创建集装箱 */
export const createDefaultContainer = (): Container => {
  const groupId = uuidv4()
  return {
    groupId,
    id: '',
    containerNo: '',
    containerTypeSize: '',
    emptyStatus: '',
    grossWeight: '',
    packages: '',
    productCode: '',
    createTime: 0,
    cargos: [createDefaultCargo(groupId)],
  }
}

/** 创建联系人信息 */
export const createContactInfo = (partyType: PartyType) => {
  return {
    idInfo: '',
    partyName: '',
    partyAddress: '',
    partyTaxId: '',
    partyType,
    id: '',
  }
}

/** 创建默认parties数组 - 保证固定顺序 */
export const createDefaultParties = () => {
  return DEFAULT_PARTIES_ORDER.map((partyType) => createContactInfo(partyType))
}

/** 创建默认舱单详情 */
export const createDefaultManifestModel = (): ManifestDetail => {
  const container = createDefaultContainer()
  return {
    userRemark: '',
    success: false,
    id: '',
    enterpriseId: 0,
    enterpriseName: '',
    userId: 0,
    updatedAt: 0,
    portOfLoadingCode: '',
    portOfLoading: '',
    portOfLoadingEn: '',
    portOfDischargeCode: '',
    portOfDischarge: '',
    portOfDischargeEn: '',
    carrierScac: '',
    carrierName: '',
    carrierNameEn: '',
    caatCode: '',
    masterBillNo: '',
    vesselName: '',
    vesselNameEn: '',
    vesselCode: '',
    voyageNo: '',
    containerIndicator: 'CONTAINER',
    customsSection: '',
    houseBillNo: '',
    createTime: 0,
    houseBillOperationId: '',
    parties: createDefaultParties(),
    containers: [container],
    houseBillOperation: createHouseBillOperation(),
    billStatus: createHouseBillRiskStatus(),
  }
}

// 处理数据，接口返回null导致前端渲染异常
export function initializeDetail(detail: ManifestDetail) {
  // 初始化新字段
  if (!detail.parties || detail.parties.length === 0) {
    detail.parties = createDefaultParties()
  }

  // 确保 parties 数组的长度至少为 3，并按顺序填充
  const currentPartyTypes = detail.parties.map((p) => p.partyType)
  const missingPartyTypes = DEFAULT_PARTIES_ORDER.filter(
    (type) => !currentPartyTypes.includes(type),
  )

  // 添加缺失的 parties
  missingPartyTypes.forEach((partyType) => {
    detail.parties.push(createContactInfo(partyType))
  })

  // 重新排序 parties，确保是 SN -> CO -> NO 的顺序
  detail.parties.sort((a, b) => {
    const indexA = DEFAULT_PARTIES_ORDER.indexOf(a.partyType)
    const indexB = DEFAULT_PARTIES_ORDER.indexOf(b.partyType)
    return indexA - indexB
  })

  if (!detail.containers || detail.containers.length === 0) {
    const container = createDefaultContainer()
    detail.containers = [container]
  } else {
    // 如果接口返回的数据中有顶级的 cargos 数组，需要将其分组到对应的容器中
    if ((detail as any).cargos && Array.isArray((detail as any).cargos)) {
      const topLevelCargos = (detail as any).cargos

      // 为每个容器初始化 cargos 数组
      detail.containers.forEach((container) => {
        container.cargos = []
      })

      // 将顶级 cargos 按 groupId 分组到对应的容器中
      topLevelCargos.forEach((cargo: any) => {
        const targetContainer = detail.containers.find(
          (container) => container.groupId === cargo.groupId,
        )
        if (targetContainer) {
          targetContainer.cargos.push(cargo)
        }
      })

      // 删除顶级的 cargos 属性
      delete (detail as any).cargos
    } else {
      // 如果没有顶级 cargos，确保每个容器都有 cargos 数组
      detail.containers.forEach((container) => {
        if (!container.cargos) {
          container.cargos = [createDefaultCargo(container.groupId)]
        }
      })
    }

    // 确保每个容器至少有一个货物
    detail.containers.forEach((container) => {
      if (!container.cargos || container.cargos.length === 0) {
        container.cargos = [createDefaultCargo(container.groupId)]
      }
    })
  }
}
// 将嵌套的 cargos 平铺并从 containers 中移除，用于传给后端
export const flatCargos = (detail: ManifestDetail) => {
  const flattenedCargos: any[] = []
  // 如果是散货，只传第一个箱子
  if (detail.containerIndicator === ContainerIndicator.BULK_CARGO) {
    detail.containers = [detail.containers[0]]
  }
  // 收集所有容器中的 cargos，同时设置 containerNo
  detail.containers.forEach((container) => {
    if (container.cargos && container.cargos.length > 0) {
      container.cargos.forEach((cargo) => {
        // 设置 cargo 的 containerNo 为当前容器的 containerNo
        cargo.containerNo = container.containerNo
      })
      flattenedCargos.push(...container.cargos)
    }
  })

  // 创建新的数据结构，移除 containers 中的 cargos 字段
  const result = {
    ...detail,
    cargos: flattenedCargos,
    containers: detail.containers.map((container) => {
      const { cargos: _cargos, ...containerWithoutCargos } = container
      return containerWithoutCargos
    }),
  }

  return result
}
/**
 * @description:舱单详情操作
 */
const useManifestStore = defineStore('manifest-detail', () => {
  const detail = ref<ManifestDetail>(createDefaultManifestModel())
  const disabled = ref(false)

  const setDetail = (data: ManifestDetail) => {
    // 与storage同步建立引用， 不要使用clone
    detail.value = data
  }

  const resetDetail = () => {
    detail.value = R.clone(createDefaultManifestModel())
  }

  return {
    detail,
    disabled,
    createDefaultContainer,
    createDefaultCargo,
    createContactInfo,
    createDefaultParties,
    setDetail,
    resetDetail,
    flatCargos,
  }
})

export default useManifestStore
